/**
 * Spatial Index for efficient hit testing and frustum culling.
 *
 * Wraps rbush R-tree for O(log n) spatial queries.
 */

import RBush from 'rbush';
import type { WorldRect, WorldPoint, UINode } from './types';
import * as geometry from './geometry';

/** Internal item stored in the R-tree */
interface SpatialItem {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
	nodeId: string;
}

/**
 * SpatialIndex provides efficient spatial queries over canvas nodes.
 *
 * Features:
 * - O(log n) point and rect queries
 * - Efficient bulk loading
 * - Incremental updates
 */
export class SpatialIndex {
	private tree: RBush<SpatialItem>;
	private items: Map<string, SpatialItem>;

	constructor() {
		this.tree = new RBush<SpatialItem>();
		this.items = new Map();
	}

	/**
	 * Insert a node into the index.
	 */
	insert(nodeId: string, bounds: WorldRect): void {
		// Remove existing entry if present
		this.remove(nodeId);

		const item: SpatialItem = {
			minX: bounds.x,
			minY: bounds.y,
			maxX: bounds.x + bounds.width,
			maxY: bounds.y + bounds.height,
			nodeId
		};

		this.tree.insert(item);
		this.items.set(nodeId, item);
	}

	/**
	 * Remove a node from the index.
	 */
	remove(nodeId: string): void {
		const item = this.items.get(nodeId);
		if (item) {
			this.tree.remove(item);
			this.items.delete(nodeId);
		}
	}

	/**
	 * Update a node's bounds in the index.
	 */
	update(nodeId: string, bounds: WorldRect): void {
		this.insert(nodeId, bounds);
	}

	/**
	 * Query nodes intersecting a rectangle.
	 * Used for frustum culling and selection rectangles.
	 */
	queryRect(rect: WorldRect): string[] {
		const results = this.tree.search({
			minX: rect.x,
			minY: rect.y,
			maxX: rect.x + rect.width,
			maxY: rect.y + rect.height
		});

		return results.map((item) => item.nodeId);
	}

	/**
	 * Query nodes containing a point.
	 * Used for hit testing (click detection).
	 * Returns nodes in no particular order - caller should sort by z if needed.
	 */
	queryPoint(point: WorldPoint): string[] {
		// Query with a tiny rect around the point
		const results = this.tree.search({
			minX: point.x,
			minY: point.y,
			maxX: point.x,
			maxY: point.y
		});

		return results.map((item) => item.nodeId);
	}

	/**
	 * Rebuild the index from a list of nodes.
	 * More efficient than individual inserts for large changes.
	 */
	rebuild(nodes: UINode[]): void {
		this.clear();

		const items: SpatialItem[] = nodes.map((node) => {
			const bounds = geometry.getNodeBounds(node.position, node.size);
			return {
				minX: bounds.x,
				minY: bounds.y,
				maxX: bounds.x + bounds.width,
				maxY: bounds.y + bounds.height,
				nodeId: node.id
			};
		});

		// Bulk load is more efficient than individual inserts
		this.tree.load(items);

		// Update items map
		for (const item of items) {
			this.items.set(item.nodeId, item);
		}
	}

	/**
	 * Clear all items from the index.
	 */
	clear(): void {
		this.tree.clear();
		this.items.clear();
	}

	/**
	 * Check if a node is in the index.
	 */
	has(nodeId: string): boolean {
		return this.items.has(nodeId);
	}

	/**
	 * Get the number of items in the index.
	 */
	get size(): number {
		return this.items.size;
	}

	/**
	 * Get all bounds in the index (for debug visualization).
	 */
	getAllBounds(): WorldRect[] {
		const bounds: WorldRect[] = [];
		for (const item of this.items.values()) {
			bounds.push({
				x: item.minX,
				y: item.minY,
				width: item.maxX - item.minX,
				height: item.maxY - item.minY
			});
		}
		return bounds;
	}
}

/**
 * Create a new spatial index.
 */
export function createSpatialIndex(): SpatialIndex {
	return new SpatialIndex();
}
